import { useEffect, useRef, useState } from "react";
import { Building2, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { estApi } from "../lib/api";

/** One company picker for the whole app: type-ahead over the Directory; unknown name → "New — add to Directory". */
export default function PartyField({ value, onChange, type = "gc", placeholder = "Company", className = "", testId = "party-field", disabled = false }) {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState({ company: null, suggestions: [] });
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);
  const box = useRef(null);
  useEffect(() => { setQ(value || ""); }, [value]);
  useEffect(() => {
    if (!open || !q.trim()) return;
    const t = setTimeout(() => estApi.getCompanies({ search: q.trim(), limit: 6 }).then((r) => {
      const list = r.companies || [];
      const exact = list.find((c) => c.name.toLowerCase() === q.trim().toLowerCase()) || null;
      setRes({ company: exact, suggestions: list.filter((c) => c !== exact) });
    }).catch(() => {}), 200);
    return () => clearTimeout(t);
  }, [q, open]);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc); return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const pick = (c) => { setQ(c.name); setPicked(c); setOpen(false); onChange?.(c.name, c); };
  const known = picked && picked.name.toLowerCase() === q.trim().toLowerCase() ? picked : res.company;
  const addNew = async () => {
    setBusy(true);
    try {
      const r = await estApi.acceptParty({ company_name: q.trim(), type, source: "ui" });
      toast.success(`${r.company.name} added to the Directory`);
      pick(r.company);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that company"); } finally { setBusy(false); }
  };
  const isNew = q.trim() && !known;
  return (
    <div ref={box} className={`relative ${className}`} data-testid={testId}>
      <Input value={q} disabled={disabled} placeholder={placeholder} data-testid={`${testId}-input`}
             onChange={(e) => { setQ(e.target.value); setOpen(true); onChange?.(e.target.value, null); }} onFocus={() => setOpen(true)}
             className="h-8 text-xs pr-7" />
      {known && <Check className="absolute right-2 top-2 h-3.5 w-3.5 text-emerald-600" title="In the Directory" data-testid={`${testId}-known`} />}
      {open && q.trim() && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover p-1 text-xs shadow-md" data-testid={`${testId}-menu`}>
          {[res.company, ...res.suggestions].filter(Boolean).map((c) => (
            <button key={c.id} type="button" onClick={() => pick(c)} data-testid={`${testId}-option-${c.id}`}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-muted">
              <Building2 className="h-3 w-3 text-muted-foreground" /> <span className="flex-1 truncate">{c.name}</span>
              <span className="text-[10px] text-muted-foreground">{(c.types || []).join(", ")}</span>
            </button>))}
          {isNew && (
            <button type="button" onClick={addNew} disabled={busy} data-testid={`${testId}-add-new`}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[#007BFF] hover:bg-[#007BFF]/5">
              <Plus className="h-3 w-3" /> New — add “{q.trim()}” to the Directory as {type.toUpperCase()}
            </button>)}
        </div>)}
    </div>
  );
}
